import {
    ButtonGradientPrimary,
    Card,
    CollapsibleSection,
    Colors,
    Icon,
    Spinner,
} from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PillarAlignmentSettings from './PillarAlignmentSettings';
import PillarCard from './PillarCard';
import { UnalignedObjectivesPill } from './UnalignedObjectivesCount';
import { VISION_SECTION_TITLE } from './visionValuesStyles';

import type { PillarPayload } from '@/components/AddEditPillarModal/AddEditPillarModal';
import type { Pillar } from '@/types/pillars';

import {
    useCreatePillar,
    useGetPillarObjectiveAlignment,
    useGetPillars,
    useUpdatePillar,
} from '@/api/pillars';
import AddEditPillarModal from '@/components/AddEditPillarModal/AddEditPillarModal';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { isPillarActiveLimitError, showPillarActiveLimitToast } from '@/utils/pillarErrors';

/**
 * Horizontal padding of the settings panel, matching the header of `CollapsibleSection`'s `card`
 * variant so the switches line up under the bar that opens them.
 */
const SETTINGS_BODY_PADDING = 'px-8 pt-1 pb-5';

export default function PillarsSection() {
    const { t } = useTranslation();
    const { handleError } = useError();
    const { data: pillars = [], isLoading, error } = useGetPillars();
    const createPillar = useCreatePillar();
    const updatePillar = useUpdatePillar();

    const { data: alignment } = useGetPillarObjectiveAlignment();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPillar, setEditingPillar] = useState<Pillar | undefined>();
    const [areSettingsOpen, setAreSettingsOpen] = useState(false);

    /**
     * Collapsed, the header only warns when objectives are actually left without a pillar. An
     * unknown count — still loading, or failed — shows nothing rather than shifting the header for
     * an answer we do not have, or alarming over a count nobody asked to see.
     */
    const unalignedObjectivesCount = alignment?.unalignedObjectivesCount ?? 0;
    const isWarningShown = !areSettingsOpen && unalignedObjectivesCount > 0;

    const handleAddPillar = () => {
        setEditingPillar(undefined);
        setIsModalOpen(true);
    };

    const handleEditPillar = (pillar: Pillar) => {
        setEditingPillar(pillar);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPillar(undefined);
    };

    const addPillarIcon = (
        <Icon
            name={'essential-add'}
            size={'md'}
            strokeWidth={3}
            color={Colors.WHITE}
        />
    );

    const handleSavePillar = async (data: PillarPayload) => {
        try {
            if (editingPillar) {
                await updatePillar.mutateAsync({ id: editingPillar.id, request: data });
            } else {
                await createPillar.mutateAsync(data);
            }
            handleCloseModal();
            showSuccessToast({ message: t('pillars.saveSuccess') });
        } catch (saveError) {
            if (isPillarActiveLimitError(saveError)) {
                showPillarActiveLimitToast();
                return;
            }
            handleError({ error: saveError });
        }
    };

    const renderPillars = () => {
        if (isLoading) {
            return (
                <div className={'flex justify-center py-10'}>
                    <Spinner dataTestId={'pillars-loader'} />
                </div>
            );
        }

        if (error) {
            return (
                <p
                    role={'alert'}
                    className={'py-6 text-red-500'}
                >
                    {t('pillars.errorLoadingPillars')}
                </p>
            );
        }

        if (pillars.length === 0) {
            return (
                <div className={'flex flex-col items-center gap-6 py-12 text-center'}>
                    <p className={'text-gray-500'}>{t('pillars.noPillars')}</p>

                    <ButtonGradientPrimary
                        onClick={handleAddPillar}
                        size={'lg'}
                        fitContent
                        leftIcon={addPillarIcon}
                        dataTestId={'add-pillar-button'}
                    >
                        {t('pillars.addPillar')}
                    </ButtonGradientPrimary>
                </div>
            );
        }

        return (
            <div className={'flex flex-col gap-[24px]'}>
                {pillars.map((pillar) => (
                    <PillarCard
                        key={pillar.id}
                        pillar={pillar}
                        onEdit={() => handleEditPillar(pillar)}
                    />
                ))}
            </div>
        );
    };

    return (
        <Card
            padding={'xl'}
            borderRadius={'2xl'}
            dataTestId={'pillars-section'}
        >
            <div className={'w-full'}>
                {/* Header and settings share one block: the bar sits closer to the title it
                    belongs to than to the pillar tiles below — roughly half the distance. */}
                <div className={'mb-10'}>
                    <div
                        className={
                            'flex flex-col items-center gap-5 sm:flex-row sm:items-start sm:gap-3'
                        }
                    >
                        <div
                            className={
                                'flex w-fit flex-shrink-0 items-center justify-center !rounded-lg bg-gray-100 p-4 shadow-sm'
                            }
                        >
                            <Icon
                                name={'cryptocompany-target'}
                                size={35}
                                colorClassName={'text-teal-400'}
                                strokeWidth={2}
                            />
                        </div>

                        <div
                            className={
                                'flex flex-1 flex-col items-center gap-1.5 text-center sm:!items-start sm:!text-left'
                            }
                        >
                            <h2 className={VISION_SECTION_TITLE}>{t('pillars.title')}</h2>

                            <p className={'text-base font-medium text-gray-500'}>
                                {t('pillars.description')}
                            </p>
                        </div>

                        {isWarningShown && (
                            <div className={'flex flex-shrink-0 items-center'}>
                                <UnalignedObjectivesPill
                                    count={unalignedObjectivesCount}
                                    dataTestId={'unaligned-objectives-warning'}
                                />
                            </div>
                        )}
                    </div>

                    <CollapsibleSection
                        isOpen={areSettingsOpen}
                        onToggle={() => setAreSettingsOpen((wasOpen) => !wasOpen)}
                        variant={'card'}
                        className={'mt-4'}
                        header={
                            <span className={'flex items-center gap-2.5'}>
                                <Icon
                                    name={'settings-setting-2'}
                                    size={'md'}
                                    color={Colors.BASE_400}
                                    ariaHidden
                                />

                                <span
                                    className={
                                        'text-base-700 text-[13px] leading-[19px] font-semibold'
                                    }
                                >
                                    {t('pillars.settings.title')}
                                </span>
                            </span>
                        }
                        dataTestId={'pillar-settings'}
                    >
                        <div className={SETTINGS_BODY_PADDING}>
                            <PillarAlignmentSettings />
                        </div>
                    </CollapsibleSection>
                </div>

                {renderPillars()}

                {/* An empty list carries its own centered button; while the list is unknown there
                    is nothing to add to yet. */}
                {pillars.length > 0 && (
                    <div className={'mt-8 flex justify-start'}>
                        <ButtonGradientPrimary
                            onClick={handleAddPillar}
                            size={'md'}
                            leftIcon={addPillarIcon}
                            dataTestId={'add-pillar-button'}
                        >
                            {t('pillars.addPillar')}
                        </ButtonGradientPrimary>
                    </div>
                )}
            </div>

            <AddEditPillarModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSavePillar}
                pillar={editingPillar}
                existingPillars={pillars}
            />
        </Card>
    );
}
